import { App } from 'antd';
import { useMsal } from '@azure/msal-react';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CalendarSlot,
  PreSalesFieldSalesCalendarSlot,
  PreSalesFieldSalesSlot,
  Slot,
} from '../../../dto/model';
import { scopes } from '../../../authConfig';
import { strings } from '../../../lang';
import {
  heatBookingPageService,
  mapPreSalesFieldSalesSlots,
} from '../../../services/heat-booking-page-service';
import { GetAccessToken } from '../../../utils/auth-utils';

type UsePreSalesFieldSalesBookingParams = {
  crmId?: string;
  zipCode?: string;
};

export const usePreSalesFieldSalesBooking = ({
  crmId,
  zipCode,
}: UsePreSalesFieldSalesBookingParams) => {
  const { notification } = App.useApp();
  const { instance, inProgress } = useMsal();
  const [bookedSlot, setBookedSlot] = useState<Slot | null>(null);
  const [hasConfirmedAgentOnly, setHasConfirmedAgentOnly] = useState(false);
  const [isConfirmingAgentOnly, setIsConfirmingAgentOnly] = useState(false);
  const confirmationSeqRef = useRef(0);
  const slotsCacheRef = useRef<{ zipCode: string; slots: PreSalesFieldSalesSlot[] } | null>(null);
  const slotsRequestRef = useRef<{
    zipCode: string;
    promise: Promise<PreSalesFieldSalesSlot[]>;
  } | null>(null);
  const pendingBookingRef = useRef<{ dealId: string; slot: Slot } | null>(null);
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      confirmationSeqRef.current += 1;
    };
  }, []);

  useEffect(() => {
    slotsCacheRef.current = null;
    slotsRequestRef.current = null;
  }, [zipCode]);

  const reset = useCallback(() => {
    confirmationSeqRef.current += 1;
    slotsRequestRef.current = null;
    pendingBookingRef.current = null;
    setBookedSlot(null);
    setHasConfirmedAgentOnly(false);
    setIsConfirmingAgentOnly(false);
  }, []);

  useEffect(() => {
    reset();
  }, [crmId, reset]);

  const clearResult = useCallback(() => {
    setBookedSlot(null);
    setHasConfirmedAgentOnly(false);
  }, []);

  const cancelAgentOnlyConfirmation = useCallback(() => {
    confirmationSeqRef.current += 1;
    setIsConfirmingAgentOnly(false);
    setHasConfirmedAgentOnly(false);
  }, []);

  const confirmAgentOnly = useCallback(async () => {
    if (!crmId) {
      notification.error({ message: strings.preSalesQuestionnaire.fieldSalesConfirmationMissingDeal });
      return;
    }

    const confirmationSeq = ++confirmationSeqRef.current;
    setIsConfirmingAgentOnly(true);
    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const confirmation = await heatBookingPageService.confirmPreSalesFieldSalesBooking(crmId, token.accessToken);
      if (!isMountedRef.current || confirmationSeq !== confirmationSeqRef.current) {
        return;
      }

      if (!confirmation.success) {
        notification.error({
          message: strings.preSalesQuestionnaire.fieldSalesConfirmationError,
          description: confirmation.error ?? confirmation.message,
        });
        return;
      }

      setHasConfirmedAgentOnly(true);
      notification.success({ message: strings.preSalesQuestionnaire.fieldSalesBookingSuccess });
    } catch (error: any) {
      if (isMountedRef.current && confirmationSeq === confirmationSeqRef.current) {
        notification.error({
          message: strings.preSalesQuestionnaire.fieldSalesConfirmationError,
          description: error?.message,
        });
      }
    } finally {
      if (isMountedRef.current && confirmationSeq === confirmationSeqRef.current) {
        setIsConfirmingAgentOnly(false);
      }
    }
  }, [crmId, inProgress, instance, notification]);

  const loadSlots = useCallback(async (calendarWeek: string, accessToken: string) => {
    if (!zipCode) {
      throw new Error(strings.preSalesQuestionnaire.fieldSalesMissingZipCode);
    }

    const cachedSlots = slotsCacheRef.current;
    if (cachedSlots?.zipCode === zipCode) {
      return {
        slots: mapPreSalesFieldSalesSlots(cachedSlots.slots, calendarWeek),
      };
    }

    let request = slotsRequestRef.current;
    if (request?.zipCode !== zipCode) {
      const promise = heatBookingPageService.getPreSalesFieldSalesSlots(zipCode, accessToken).then((response) => {
        if (!response.success) {
          throw new Error(response.error ?? response.message ?? strings.preSalesQuestionnaire.fieldSalesSlotsError);
        }

        return response.data ?? [];
      });
      request = { zipCode, promise };
      slotsRequestRef.current = request;
    }

    try {
      const slots = await request.promise;
      if (slotsRequestRef.current === request) {
        slotsCacheRef.current = { zipCode, slots };
        slotsRequestRef.current = null;
      }

      return {
        slots: mapPreSalesFieldSalesSlots(slots, calendarWeek),
      };
    } catch (error) {
      if (slotsRequestRef.current === request) {
        slotsRequestRef.current = null;
      }
      throw error;
    }
  }, [zipCode]);

  const bookSlot = useCallback(async (slot: CalendarSlot, accessToken: string) => {
    const fieldSalesSlot = (slot as PreSalesFieldSalesCalendarSlot).fieldSalesSlots?.[0];
    if (!crmId || !zipCode || !fieldSalesSlot) {
      return {
        isSuccess: false,
        errors: [strings.preSalesQuestionnaire.fieldSalesBookingMissingData],
        data: {} as Slot,
      };
    }

    let nextBookedSlot = pendingBookingRef.current?.dealId === crmId
      ? pendingBookingRef.current.slot
      : null;

    if (!nextBookedSlot) {
      const response = await heatBookingPageService.bookPreSalesFieldSalesSlot(
        {
          dealId: crmId,
          slotId: fieldSalesSlot.slotId,
          zipCode,
        },
        accessToken,
      );

      if (!response.success) {
        return {
          isSuccess: false,
          errors: [response.error ?? response.message ?? strings.preSalesQuestionnaire.fieldSalesBookingError],
          data: {} as Slot,
        };
      }

      nextBookedSlot = {
        id: 0,
        startDate: dayjs(fieldSalesSlot.start),
        endDate: dayjs(fieldSalesSlot.end),
        salesManagerName: fieldSalesSlot.sellerName,
        externalType: 'FieldSales',
        externalStatus: 'Booked',
        meetingUrl: '',
        metaBookingInfo: '',
        externalId: response.data?.slotId ?? fieldSalesSlot.slotId,
      };
      pendingBookingRef.current = { dealId: crmId, slot: nextBookedSlot };
    }

    const confirmation = await heatBookingPageService.confirmPreSalesFieldSalesBooking(crmId, accessToken);
    if (!confirmation.success) {
      return {
        isSuccess: false,
        errors: [confirmation.error ?? confirmation.message ?? strings.preSalesQuestionnaire.fieldSalesBookingConfirmationError],
        data: nextBookedSlot,
      };
    }

    pendingBookingRef.current = null;
    return {
      isSuccess: true,
      errors: [],
      data: nextBookedSlot,
    };
  }, [crmId, zipCode]);

  const handleBooking = useCallback((isSuccess: boolean, nextBookedSlot?: Slot, error?: string) => {
    if (!isMountedRef.current) {
      return;
    }

    if (isSuccess && nextBookedSlot) {
      setBookedSlot({
        ...nextBookedSlot,
        startDate: dayjs(nextBookedSlot.startDate),
        endDate: dayjs(nextBookedSlot.endDate),
      });
      notification.success({ message: strings.preSalesQuestionnaire.fieldSalesBookingSuccess });
      return;
    }

    if (error) {
      notification.error({
        message: strings.preSalesQuestionnaire.fieldSalesBookingError,
        description: error,
      });
    }
  }, [notification]);

  return {
    bookedSlot,
    hasConfirmedAgentOnly,
    isConfirmingAgentOnly,
    loadSlots,
    bookSlot,
    handleBooking,
    confirmAgentOnly,
    cancelAgentOnlyConfirmation,
    clearResult,
    hasPendingBooking: () => pendingBookingRef.current !== null,
  };
};
